import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import {
  useBulkDeleteContacts,
  useContacts,
  useDeleteContact,
  useGenerateMatchScore,
  useUploadCSV,
  useContactGroups,
  useBulkUpdateContactConsent,
} from "@/features/contacts/hooks/useContacts";
import { DEFAULT_FILTERS } from "@/shared/utils/utils";
import {
  buildURLFromFilters,
  parseFiltersFromURL,
  parseGroupByFromURL,
} from "@/shared/utils/filterUrl";
import {
  compareContactMatchFitGroupLabels,
  compareContactCreatedDateGroupLabels,
} from "@/shared/utils/contactConfig";
import { useMainStore } from "@/stores";
import { useDynamicRowsPerPage } from "@/shared/hooks/useDynamicRowsPerPage";
import { sortData } from "@/shared/utils/sortUtils";
import {
  fetchAllContactIdsAPI,
  previewCSVAPI,
  uploadContactsCSV,
  fetchContactsAPI,
  fetchGroupContactsAPI,
} from "@/features/contacts/api/contactsApi";
import { showError, showWarning, showSuccess } from "@/shared/utils/toast";
// import { getMatchScoreFitBucketKey } from "@/shared/utils/helperFunctions";
import useMoveContacts from "@/features/contacts/hooks/useMoveContacts";
import { useHasNoCampaigns } from "@/features/campaigns/hooks/useCampaign";
import {
  GROUP_BATCH_SELECT_SIZE,
  pickNextUnselected,
} from "@/features/contacts/utils/groupSelection";

/** Matches server cap on contact ID list fetch (select-all). */
const CONTACTS_SELECTION_LIMIT = 10_000;

const useContactsPage = () => {
  const queryClient = useQueryClient();
  const user = useMainStore((state) => state.user);
  const [searchParams, setSearchParams] = useSearchParams();
  const isInternalURLUpdate = useRef(false);
  const isReadingFromURL = useRef(false);
  const isFirstRender = useRef(true);

  const rowsPerPage = useDynamicRowsPerPage(30);
  const [searchValue, setSearchValue] = useState("");
  const [appliedFilters, setAppliedFilters] = useState({});
  const [filters, setFilters] = useState(DEFAULT_FILTERS);

  const [show, setShow] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showfilters, setShowfilters] = useState(false);
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [deletingContactId, setDeletingContactId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [showAddContactForm, setShowAddContactForm] = useState(false);
  const [groupBy, setGroupBy] = useState(null);
  const [isEntireListSelected, setIsEntireListSelected] = useState(false);
  const [selectedGroupIds, setSelectedGroupIds] = useState(new Set());
  const [fieldMappingOpen, setFieldMappingOpen] = useState(false);
  const [importFileForMapping, setImportFileForMapping] = useState(null);
  const [csvPreviewData, setCsvPreviewData] = useState(null);
  const [showFastProspectingDrawer, setShowFastProspectingDrawer] = useState(false);
  const [showEditContactForm, setShowEditContactForm] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [showConsentModal, setShowConsentModal] = useState(false);
  const [consentTargets, setConsentTargets] = useState([]);


  const selectedContacts = useMainStore((state) => state.selectedContacts);
  const setSelectedContacts = useMainStore(
    (state) => state.setSelectedContacts,
  );
  const _setContactsToAddToCampaign = useMainStore(
    (state) => state.setContactsToAddToCampaign,
  );
  const clearContactsToAddToCampaign = useMainStore(
    (state) => state.clearContactsToAddToCampaign,
  );
  const {
    mutate: updateBulkConsent,
    isPending: isUpdatingConsent,
  } = useBulkUpdateContactConsent();

  const appliedFiltersForApi = appliedFilters;

  const isGrouped = Boolean(groupBy);

  const {
    contacts: apiContacts = [],
    totalCount: apiTotalCount,
    isLoading: isLoadingContacts,
    isFetching: isFetchingContacts,
    refetch,
  } = useContacts(
    currentPage,
    rowsPerPage,
    "",
    searchValue,
    appliedFiltersForApi,
    !isGrouped,
  );

  const {
    data: groupsData,
    isLoading: isLoadingGroups,
    isFetching: isFetchingGroups,
  } = useContactGroups(
    groupBy,
    currentPage,
    rowsPerPage,
    searchValue,
    appliedFiltersForApi,
    isGrouped,
  );

  const displayGroups = useMemo(() => {
    if (!isGrouped) return [];
    const groups = groupsData?.groups ?? [];
    if (groupBy === "match_fit") {
      return [...groups].sort((a, b) =>
        compareContactMatchFitGroupLabels(a.name, b.name)
      );
    }
    if (groupBy === "created_date" || groupBy === "created_at") {
      return [...groups].sort((a, b) =>
        compareContactCreatedDateGroupLabels(a.name, b.name)
      );
    }
    return groups;
  }, [isGrouped, groupsData, groupBy]);

  const totalGroups = useMemo(() => {
    if (!isGrouped) return 0;
    return groupsData?.total ?? 0;
  }, [isGrouped, groupsData]);

  const [expandedGroups, setExpandedGroups] = useState({});
  const [openGroups, setOpenGroups] = useState(new Set());
  const [sortDescriptor, setSortDescriptor] = useState({ column: "", direction: null });

  useEffect(() => {
    setExpandedGroups({});
    setOpenGroups(new Set());
  }, [searchValue, appliedFilters, groupBy, sortDescriptor]);

  const toggleGroupExpand = useCallback(async (groupId, autoSelect = false) => {
    const isCurrentlyOpen = openGroups.has(groupId);
    const nextOpenGroups = new Set(openGroups);

    if (isCurrentlyOpen && !autoSelect) {
      nextOpenGroups.delete(groupId);
      setOpenGroups(nextOpenGroups);
    } else {
      if (!isCurrentlyOpen) {
        nextOpenGroups.add(groupId);
        setOpenGroups(nextOpenGroups);
      }

      if (!expandedGroups[groupId]) {
        setExpandedGroups((prev) => ({
          ...prev,
          [groupId]: { contacts: [], nextCursor: null, hasMore: false, loading: true }
        }));

        try {
          const res = await fetchGroupContactsAPI(
            groupBy,
            groupId,
            20,
            null,
            searchValue,
            appliedFiltersForApi,
            sortDescriptor
          );

          setExpandedGroups((prev) => ({
            ...prev,
            [groupId]: {
              contacts: res.contacts,
              nextCursor: res.nextCursor,
              hasMore: res.hasMore,
              loading: false
            }
          }));

          if (autoSelect) {
            const currentSelected = useMainStore.getState().selectedContacts || [];
            const existingIds = new Set(currentSelected.map((c) => c.id));
            const toAdd = res.contacts
              .filter((c) => !existingIds.has(c.id))
              .map((c) => ({
                id: c.id,
                name: c.name,
                email: c.email,
                company: c.company,
              }));
            setSelectedContacts([...currentSelected, ...toAdd]);
          }
        } catch (err) {
          showError(err.message || "Failed to load contacts for group.");
          setExpandedGroups((prev) => ({
            ...prev,
            [groupId]: { contacts: [], nextCursor: null, hasMore: false, loading: false }
          }));
        }
      } else if (autoSelect) {
        const contacts = expandedGroups[groupId]?.contacts || [];
        const currentSelected = useMainStore.getState().selectedContacts || [];
        const existingIds = new Set(currentSelected.map((c) => c.id));
        const toAdd = contacts
          .filter((c) => !existingIds.has(c.id))
          .map((c) => ({
            id: c.id,
            name: c.name,
            email: c.email,
            company: c.company,
          }));
        setSelectedContacts([...currentSelected, ...toAdd]);
      }
    }
  }, [openGroups, expandedGroups, groupBy, searchValue, appliedFiltersForApi, sortDescriptor, setSelectedContacts]);

  const [batchSelectingGroupId, setBatchSelectingGroupId] = useState(null);

  // Adds the next 20 unselected contacts of a group (display order), loading more of the
  // group's pages if needed, and opens the group so the user sees exactly who was added.
  const selectNextInGroup = useCallback(async (groupId, groupLabel = "this group") => {
    const currentSelected = useMainStore.getState().selectedContacts || [];
    const capacity = CONTACTS_SELECTION_LIMIT - currentSelected.length;
    if (capacity <= 0) {
      showWarning(`Only ${CONTACTS_SELECTION_LIMIT.toLocaleString()} contacts can be selected at a time.`);
      return;
    }
    const want = Math.min(GROUP_BATCH_SELECT_SIZE, capacity);
    const selectedIds = new Set(currentSelected.map((c) => c.id));

    const existing = expandedGroups[groupId];
    let contacts = Array.isArray(existing?.contacts) ? existing.contacts : [];
    let nextCursor = existing?.nextCursor ?? null;
    let hasMore = existing ? Boolean(existing.hasMore) : true;
    let loadedOnce = Boolean(existing) && !existing.loading;

    setBatchSelectingGroupId(groupId);
    try {
      let picked = pickNextUnselected(contacts, selectedIds, want);
      while (picked.length < want && (!loadedOnce || (hasMore && nextCursor))) {
        const res = await fetchGroupContactsAPI(
          groupBy,
          groupId,
          GROUP_BATCH_SELECT_SIZE,
          loadedOnce ? nextCursor : null,
          searchValue,
          appliedFiltersForApi,
          sortDescriptor
        );
        contacts = loadedOnce ? [...contacts, ...(res.contacts || [])] : res.contacts || [];
        nextCursor = res.nextCursor ?? null;
        hasMore = Boolean(res.hasMore);
        loadedOnce = true;
        picked = pickNextUnselected(contacts, selectedIds, want);
      }

      setExpandedGroups((prev) => ({
        ...prev,
        [groupId]: { contacts, nextCursor, hasMore, loading: false },
      }));
      setOpenGroups((prev) => new Set(prev).add(groupId));

      if (picked.length === 0) {
        showWarning(`Every contact in ${groupLabel} is already selected.`);
        return;
      }
      const latest = useMainStore.getState().selectedContacts || [];
      const latestIds = new Set(latest.map((c) => c.id));
      const toAdd = picked
        .filter((c) => !latestIds.has(c.id))
        .map((c) => ({ id: c.id, name: c.name, email: c.email, company: c.company }));
      setSelectedContacts([...latest, ...toAdd]);
      showSuccess(
        `Selected ${toAdd.length} contact${toAdd.length === 1 ? "" : "s"} in ${groupLabel} (${(latest.length + toAdd.length).toLocaleString()} selected in total).`
      );
    } catch (err) {
      showError(err.message || "Failed to select contacts in this group.");
    } finally {
      setBatchSelectingGroupId(null);
    }
  }, [expandedGroups, groupBy, searchValue, appliedFiltersForApi, sortDescriptor, setSelectedContacts]);

  const loadMoreGroupContacts = useCallback(async (groupId) => {
    const groupState = expandedGroups[groupId];
    if (!groupState || groupState.loading || !groupState.hasMore || !groupState.nextCursor) return;

    setExpandedGroups((prev) => ({
      ...prev,
      [groupId]: { ...prev[groupId], loading: true }
    }));

    try {
      const res = await fetchGroupContactsAPI(
        groupBy,
        groupId,
        20,
        groupState.nextCursor,
        searchValue,
        appliedFiltersForApi,
        sortDescriptor
      );

      setExpandedGroups((prev) => ({
        ...prev,
        [groupId]: {
          contacts: [...groupState.contacts, ...res.contacts],
          nextCursor: res.nextCursor,
          hasMore: res.hasMore,
          loading: false
        }
      }));
    } catch (err) {
      showError(err.message || "Failed to load more contacts for group.");
      setExpandedGroups((prev) => ({
        ...prev,
        [groupId]: { ...prev[groupId], loading: false }
      }));
    }
  }, [expandedGroups, groupBy, searchValue, appliedFiltersForApi, sortDescriptor]);

  const refreshOpenGroups = useCallback(async () => {
    if (openGroups.size === 0) return;

    for (const groupId of openGroups) {
      setExpandedGroups((prev) => ({
        ...prev,
        [groupId]: { ...(prev[groupId] || {}), loading: true }
      }));

      try {
        const res = await fetchGroupContactsAPI(
          groupBy,
          groupId,
          20,
          null,
          searchValue,
          appliedFiltersForApi,
          sortDescriptor
        );

        setExpandedGroups((prev) => ({
          ...prev,
          [groupId]: {
            contacts: res.contacts,
            nextCursor: res.nextCursor,
            hasMore: res.hasMore,
            loading: false
          }
        }));
      } catch (err) {
        showError(err.message || `Failed to refresh contacts for group ${groupId}`);
        setExpandedGroups((prev) => ({
          ...prev,
          [groupId]: { ...(prev[groupId] || {}), loading: false }
        }));
      }
    }
  }, [openGroups, groupBy, searchValue, appliedFiltersForApi, sortDescriptor]);

  const wasFetchingGroupsRef = useRef(false);

  useEffect(() => {
    if (isFetchingGroups) {
      wasFetchingGroupsRef.current = true;
    } else if (wasFetchingGroupsRef.current) {
      wasFetchingGroupsRef.current = false;
      refreshOpenGroups();
    }
  }, [isFetchingGroups, refreshOpenGroups]);

  const { mutateAsync: uploadCSV, isPending: isUploading } = useUploadCSV();
  const { mutate: deleteContact, isPending: isDeleting } = useDeleteContact(
    () => setShowDeleteConfirmModal(false),
  );

  const { mutate: bulkDeleteContacts, isPending: isBulkDeleting } =
    useBulkDeleteContacts();
  const { mutate: generateMatchScore, isPending: isGeneratingMatchScore } = useGenerateMatchScore();

  const displayContacts = useMemo(() => {
    const raw = apiContacts ?? [];
    if (!sortDescriptor.column || !sortDescriptor.direction) {
      return raw;
    }
    return sortData(raw, sortDescriptor.column, sortDescriptor.direction);
  }, [apiContacts, sortDescriptor.column, sortDescriptor.direction]);

  const totalContacts = apiTotalCount ?? 0;

  /** Same cap as `selectEntireFilteredList` — UI must not promise more than this many. */
  const maxSelectableFromFilteredList = useMemo(
    () => Math.min(totalContacts, CONTACTS_SELECTION_LIMIT),
    [totalContacts],
  );

  const allContactsOnPageSelected = useMemo(() => {
    const pageLen = displayContacts.length;
    if (!pageLen) return false;
    const pageIds = displayContacts.map((c) => c.id);
    const selectedIds = new Set(selectedContacts.map((c) => c.id));
    const allOnPage = pageIds.every((id) => selectedIds.has(id));
    return allOnPage && selectedContacts.length >= pageLen;
  }, [displayContacts, selectedContacts]);

  const paginationTotalCount = isGrouped ? totalGroups : totalContacts;

  const totalPages =
    rowsPerPage > 0 ? Math.max(1, Math.ceil(paginationTotalCount / rowsPerPage)) : 1;

  useEffect(() => {
    const isFetching = isGrouped ? isFetchingGroups : isFetchingContacts;
    if (isFetching) return;

    const hasTotalCount = isGrouped
      ? groupsData !== undefined && groupsData !== null
      : apiTotalCount !== undefined && apiTotalCount !== null;
    if (!hasTotalCount) return;

    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages, isGrouped, isFetchingContacts, isFetchingGroups, groupsData, apiTotalCount]);

  useEffect(() => {
    setCurrentPage(1);
  }, [rowsPerPage]);

  // ── URL → State (reactive: mount, back/forward, manual edit) ──
  useEffect(() => {
    if (isInternalURLUpdate.current) {
      isInternalURLUpdate.current = false;
      return;
    }

    isReadingFromURL.current = true;

    const urlSearch = searchParams.get("search") || "";
    const urlFilters = parseFiltersFromURL(searchParams);
    setSearchValue(urlSearch);
    setAppliedFilters(urlFilters);
    setFilters({ ...DEFAULT_FILTERS, ...urlFilters });
    setGroupBy(parseGroupByFromURL(searchParams));
  }, [searchParams]);

  // ── State → URL ──
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (isReadingFromURL.current) {
      isReadingFromURL.current = false;
      return;
    }

    isInternalURLUpdate.current = true;
    const queryString = buildURLFromFilters({
      filters: appliedFilters,
      searchValue,
      groupBy,
    });
    setSearchParams(queryString, { replace: true });
  }, [appliedFilters, searchValue, groupBy, setSearchParams]);

  // ── Selection ──

  const prevSearchRef = useRef(searchValue);
  const prevFiltersRef = useRef(appliedFilters);

  useEffect(() => {
    const searchChanged = prevSearchRef.current !== searchValue;
    const filtersChanged = prevFiltersRef.current !== appliedFilters;
    prevSearchRef.current = searchValue;
    prevFiltersRef.current = appliedFilters;

    if ((searchChanged || filtersChanged) && selectedContacts.length > 0) {
      setSelectedContacts([]);
      setIsEntireListSelected(false);
      setSelectedGroupIds(new Set());
    }
  }, [searchValue, appliedFilters, selectedContacts.length, setSelectedContacts]);

  const handleSelectionChange = useCallback(
    (users) => {
      setIsEntireListSelected(false);
      const minimal = users.map((c) => ({
        id: c.id,
        name: c.name,
        email: c.email,
        company: c.company,
      }));
      setSelectedContacts(minimal);
    },
    [setSelectedContacts],
  );

  const clearSelection = useCallback(() => {
    setSelectedContacts([]);
    setIsEntireListSelected(false);
    setSelectedGroupIds(new Set());
  }, [setSelectedContacts]);

  const completeMoveToContacts = useCallback(() => {
    setSelectedContacts([]);
    setIsEntireListSelected(false);
    setSelectedGroupIds(new Set());
    clearContactsToAddToCampaign();
    queryClient.invalidateQueries({ queryKey: ["contacts"] });
    queryClient.invalidateQueries({ queryKey: ["contacts-infinite"] });
    queryClient.invalidateQueries({ queryKey: ["contact-groups"] });
    setExpandedGroups({});
    setOpenGroups(new Set());
  }, [setSelectedContacts, clearContactsToAddToCampaign, queryClient]);

  const toggleGroupSelection = useCallback(async (groupId, shouldSelect) => {
    try {
      const res = await fetchAllContactIdsAPI(searchValue, {
        ...appliedFiltersForApi,
        groupBy,
        groupId,
      });

      const currentSelected = useMainStore.getState().selectedContacts || [];
      const currentSelectedIds = new Set(currentSelected.map((c) => c.id));

      let nextSelected;
      if (shouldSelect) {
        const toAdd = res
          .filter((id) => !currentSelectedIds.has(id))
          .map((id) => ({
            id,
            name: "",
            email: "",
            company: "",
          }));
        nextSelected = [...currentSelected, ...toAdd];
        
        setSelectedGroupIds((prev) => {
          const next = new Set(prev);
          next.add(groupId);
          return next;
        });
      } else {
        const groupIdsSet = new Set(res);
        nextSelected = currentSelected.filter((c) => !groupIdsSet.has(c.id));
        
        setSelectedGroupIds((prev) => {
          const next = new Set(prev);
          next.delete(groupId);
          return next;
        });

        if (isEntireListSelected) {
          setIsEntireListSelected(false);
          setSelectedGroupIds((prev) => {
            const next = new Set(prev);
            displayGroups.forEach((g) => {
              if (g.id !== groupId) {
                next.add(g.id);
              }
            });
            return next;
          });
        }
      }

      setSelectedContacts(nextSelected);
    } catch (err) {
      showError(err.message || "Failed to toggle group selection.");
    }
  }, [groupBy, searchValue, appliedFiltersForApi, isEntireListSelected, displayGroups, setSelectedContacts]);

  const {
    handleMoveTo,
    handleCampaignSelected,
    executeCampaignMove,
    showCampaigns,
    showReview,
    reviewContacts,
    selectedCampaign,
    moveMode,
    isGeneratingMoveScores,
    isMoving,
    handleReviewBack,
    closeCampaignModal,
  } = useMoveContacts({
    selectedContacts,
    userId: user?.id,
    onComplete: completeMoveToContacts,
  });

  const selectEntireFilteredList = useCallback(async () => {
    try {
      const ids = await fetchAllContactIdsAPI(searchValue, appliedFilters);
      const capped = ids.slice(0, CONTACTS_SELECTION_LIMIT);
      const minimal = capped.map((id) => ({
        id,
        name: "",
        email: "",
        company: "",
      }));
      setSelectedContacts(minimal);
      setIsEntireListSelected(true);
      if (totalContacts > CONTACTS_SELECTION_LIMIT) {
        showWarning(
          `Only ${CONTACTS_SELECTION_LIMIT.toLocaleString()} contacts can be selected at a time. This list has ${totalContacts.toLocaleString()} — use filters or work in batches.`,
        );
      }
    } catch (e) {
      showError(e.message || "Could not select all contacts.");
    }
  }, [searchValue, appliedFilters, setSelectedContacts, totalContacts]);

  const setGroupByMode = useCallback((mode) => {
    setGroupBy((prev) => (prev === mode ? null : mode));
  }, []);

  const groupByAccounts = groupBy === "accounts";

  // ── Search ──

  const handleSearchChange = useCallback((newVal) => {
    setSearchValue(newVal);
  }, []);

  // ── Filters ──

  const applyFilters = useCallback(() => {
    const cleaned = Object.fromEntries(
      Object.entries(filters).filter(([_, v]) =>
        Array.isArray(v) ? v.length > 0 : Boolean(v),
      ),
    );
    setAppliedFilters(cleaned);
    setShowfilters(false);
  }, [filters]);

  const clearFilters = useCallback(() => {
    setFilters(DEFAULT_FILTERS);
    setAppliedFilters({});
    setSearchValue("");
    setShowfilters(false);
  }, []);

  const removeSingleFilterValue = useCallback((key, valueToRemove) => {
    setFilters((prev) => {
      const current = prev[key];
      let updated;
      if (Array.isArray(current)) {
        updated = current.filter((val) => val !== valueToRemove);
      } else {
        updated = [];
      }
      const nextFilters = { ...prev, [key]: updated };

      const cleanedApplied = Object.fromEntries(
        Object.entries(nextFilters).filter(([_, v]) =>
          Array.isArray(v) ? v.length > 0 : Boolean(v),
        ),
      );
      setAppliedFilters(cleanedApplied);
      return nextFilters;
    });
  }, []);

  const appliedFilterCount = useMemo(
    () =>
      Object.values(appliedFilters).filter((v) =>
        Array.isArray(v) ? v.length > 0 : Boolean(v),
      ).length,
    [appliedFilters],
  );

  const refetchOpenGroups = useCallback(async () => {
    if (!isGrouped || openGroups.size === 0) return;
    const openGroupIds = Array.from(openGroups);
    for (const groupId of openGroupIds) {
      try {
        const res = await fetchGroupContactsAPI(
          groupBy,
          groupId,
          20,
          null,
          searchValue,
          appliedFiltersForApi,
          sortDescriptor
        );
        setExpandedGroups((prev) => ({
          ...prev,
          [groupId]: {
            contacts: res.contacts,
            nextCursor: res.nextCursor,
            hasMore: res.hasMore,
            loading: false,
          },
        }));
      } catch (err) {
        // ignore errors during background group refetch
      }
    }
  }, [isGrouped, openGroups, groupBy, searchValue, appliedFiltersForApi, sortDescriptor]);

  // ── Delete ──

  const handleDelete = useCallback((contactId) => {
    setDeletingContactId(contactId);
    setShowDeleteConfirmModal(true);
  }, []);

  const handleDeleteContact = useCallback(() => {
    if (deletingContactId) {
      deleteContact(deletingContactId, {
        onSuccess: () => {
          setDeletingContactId(null);
          setShowDeleteConfirmModal(false);
          refetchOpenGroups();
        },
      });
    }
  }, [deletingContactId, deleteContact, refetchOpenGroups]);


  // ── Bulk Delete ──
  const handleBulkDelete = useCallback(() => {
    if (selectedContacts.length === 0) return;
    bulkDeleteContacts(
      { contactIds: selectedContacts.map((c) => c.id) },
      {
        onSuccess: () => {
          setSelectedContacts([]);
          setIsEntireListSelected(false);
          setShowDeleteConfirmModal(false);
          refetch();
          refetchOpenGroups();
        },
      }
    );
  }, [
    selectedContacts,
    bulkDeleteContacts,
    setSelectedContacts,
    setShowDeleteConfirmModal,
    refetch,
    refetchOpenGroups,
  ]);

  const handleExportSelected = useCallback(async () => {
    if (selectedContacts.length === 0) return;
    try {
      // 1. Fetch full contact details matching the search query and filters
      const response = await fetchContactsAPI(1, 10000, searchValue, appliedFilters);
      const allMatchingContacts = response?.contacts ?? [];
      
      // 2. Filter to only selected contact IDs
      const selectedIds = new Set(selectedContacts.map(c => c.id));
      const contactsToExport = allMatchingContacts.filter(c => selectedIds.has(c.id));
      
      if (contactsToExport.length === 0) {
        showError("No contact details found to export.");
        return;
      }
      
      // 3. Convert to CSV
      const headers = [
        "Full Name",
        "Email",
        "Company",
        "Designation",
        "Location",
        "Industry",
        "Company Size",
        "Linkedin URL",
        "Company LinkedIn URL",
        "Whatsapp Number",
        "Currently Enrolled Campaign",
        "Match Fit"
      ];
      
      const csvRows = [
        headers.join(","),
        ...contactsToExport.map(c => {
          const values = [
            c.name ?? "",
            c.email ?? "",
            c.company ?? "",
            c.position ?? "",
            [c.state, c.location].filter((part, i, all) => part && all.indexOf(part) === i).join(", "),
            c.industry ?? "",
            c.companySize ?? "",
            c.linkedinUrl ?? "",
            c.companyLinkedinUrl ?? "",
            c.whatsappNumber ?? "",
            c.currentlyEnrolledCampaignName ?? "",
            c.matchFitGroupLabel ?? ""
          ];
          // Escape quotes and commas in CSV values
          return values.map(val => `"${String(val).replace(/"/g, '""')}"`).join(",");
        })
      ];
      
      const csvContent = "data:text/csv;charset=utf-8," + csvRows.join("\n");
      const encodedUri = encodeURI(csvContent);
      
      // 4. Download file
      const link = document.createElement("a");
      link.setAttribute("href", encodedUri);
      link.setAttribute("download", `salesworx_contacts_export_${Date.now()}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      showSuccess(`Successfully exported ${contactsToExport.length} contacts.`);
    } catch (error) {
      showError(error.message || "Failed to export contacts.");
    }
  }, [selectedContacts, searchValue, appliedFilters]);

  // ── CSV Upload (preview + field mapping) ──

  const handleCSVFileUpload = useCallback(
    async (file) => {
      if (isUploading || !file) return false;
      try {
        const preview = await previewCSVAPI(file);
        setImportFileForMapping(file);
        setCsvPreviewData(preview);
        setFieldMappingOpen(true);
        return true;
      } catch (e) {
        showError(e.message || "Could not read CSV.");
        return false;
      }
    },
    [isUploading],
  );

  const closeFieldMappingModal = useCallback(() => {
    setFieldMappingOpen(false);
    setImportFileForMapping(null);
    setCsvPreviewData(null);
  }, []);

  const checkCSVImport = useCallback(
    async (columnMapping) => {
      try {
        return await uploadContactsCSV(importFileForMapping, columnMapping, { dryRun: true });
      } catch (e) {
        showError(e.message || "Could not check the file.");
        return null;
      }
    },
    [importFileForMapping],
  );

  const confirmCSVImportWithMapping = useCallback(
    async (columnMapping, skipMissing = []) => {
      if (!importFileForMapping) return false;
      try {
        await uploadCSV({
          file: importFileForMapping,
          columnMapping,
          skipMissing,
        });
        closeFieldMappingModal();
        return true;
      } catch {
        return false;
      }
    },
    [importFileForMapping, uploadCSV, closeFieldMappingModal],
  );

  // ── View state ──
  const viewState = useMemo(() => {
    const hasSearch = searchValue.trim().length > 0;
    const hasFilters = Object.values(appliedFilters).some((v) =>
      Array.isArray(v) ? v.length > 0 : Boolean(v),
    );
    const hasData = isGrouped ? displayGroups.length > 0 : displayContacts.length > 0;
    const loading = isGrouped ? isLoadingGroups : isLoadingContacts;

    if (loading) return "LOADING";
    if (!hasData && !hasSearch && !hasFilters) return "EMPTY_INITIAL";
    if (!hasData && hasSearch) return "EMPTY_SEARCH";
    if (!hasData && hasFilters) return "EMPTY_FILTERS";
    return "DATA";
  }, [isGrouped, isLoadingContacts, isLoadingGroups, displayContacts.length, displayGroups.length, searchValue, appliedFilters]);

  const displayLength = isGrouped ? displayGroups.length : displayContacts.length;
  const isLoading = isGrouped ? isLoadingGroups : isLoadingContacts;

  useEffect(() => {
    if (displayLength === 0 && !isLoading) {
      setShow(true);
    }
  }, [displayLength, isLoading]);

  // Match scores are measured against a campaign's audience, so they need at least one campaign.
  const hasNoCampaigns = useHasNoCampaigns();

  const handleGenerateMatchScore = useCallback(() => {
    if (selectedContacts.length === 0 || hasNoCampaigns) return;
    generateMatchScore({
      userId: user.id,
      contactIds: [...selectedContacts.map((c) => c.id)],
    });
  }, [selectedContacts, generateMatchScore, user.id, hasNoCampaigns]);

  const handleEdit = useCallback((contact) => {
    setEditingContact(contact);
    setShowEditContactForm(true);
  }, []);

  const openConsentUpdate = useCallback((contacts = selectedContacts) => {
    const contactList = Array.isArray(contacts) ? contacts : [contacts];
    const targets = contactList.filter((contact) => contact?.id);
    console.debug("[Contacts][consent-open]", {
      requestedType: Array.isArray(contacts) ? "multiple" : "single",
      targetCount: targets.length,
    });
    if (targets.length === 0) return;
    setConsentTargets(targets);
    setShowConsentModal(true);
  }, [selectedContacts]);

  const closeConsentUpdate = useCallback(() => {
    if (isUpdatingConsent) return;
    setShowConsentModal(false);
    setConsentTargets([]);
  }, [isUpdatingConsent]);

  const handleConsentUpdate = useCallback(({ status, source }) => {
    const contactIds = consentTargets.map((contact) => contact.id).filter(Boolean);
    if (contactIds.length === 0) return;
    console.debug("[Contacts][consent-submit]", {
      requestedCount: contactIds.length,
      status,
    });
    updateBulkConsent(
      { contactIds, status, source },
      {
        onSuccess: (data) => {
          console.debug("[Contacts][consent-result]", {
            requestedCount: contactIds.length,
            updatedCount: data?.count ?? 0,
          });
          setShowConsentModal(false);
          setConsentTargets([]);
          setSelectedContacts([]);
          setIsEntireListSelected(false);
          setSelectedGroupIds(new Set());
        },
      },
    );
  }, [consentTargets, setSelectedContacts, updateBulkConsent]);

  return {
    displayContacts,
    totalContacts,
    maxSelectableFromFilteredList,
    contactsSelectionLimit: CONTACTS_SELECTION_LIMIT,

    isLoadingContacts,
    isFetchingContacts,
    viewState,
    refetch,

    rowsPerPage,

    groupBy,
    setGroupByMode,
    groupByAccounts,

    isGrouped,
    displayGroups,
    totalGroups,
    expandedGroups,
    openGroups,
    toggleGroupExpand,
    loadMoreGroupContacts,
    sortDescriptor,
    setSortDescriptor,
    isLoadingGroups,
    isFetchingGroups,
    paginationTotalCount,

    isEntireListSelected,
    selectedGroupIds,
    selectEntireFilteredList,
    toggleGroupSelection,
    selectNextInGroup,
    batchSelectingGroupId,

    fieldMappingOpen,
    csvPreviewData,
    closeFieldMappingModal,
    confirmCSVImportWithMapping,
    checkCSVImport,

    allContactsOnPageSelected,

    currentPage,
    setCurrentPage,

    searchValue,
    handleSearchChange,

    filters,
    setFilters,
    appliedFilters,
    appliedFilterCount,
    showfilters,
    setShowfilters,
    applyFilters,
    clearFilters,
    removeSingleFilterValue,

    selectedContacts,
    setSelectedContacts,
    handleSelectionChange,
    clearSelection,

    handleDelete,
    handleDeleteContact,
    handleBulkDelete,
    showDeleteConfirmModal,
    setShowDeleteConfirmModal,
    deletingContactId,
    setDeletingContactId,
    isDeleting,
    isBulkDeleting,

    show,
    setShow,
    showAddModal,
    setShowAddModal,
    handleCSVFileUpload,
    isUploading,
    showAddContactForm,
    setShowAddContactForm,
    showEditContactForm,
    setShowEditContactForm,
    editingContact,
    setEditingContact,
    handleEdit,
    handleExportSelected,
    consentTargets,
    showConsentModal,
    openConsentUpdate,
    closeConsentUpdate,
    handleConsentUpdate,
    isUpdatingConsent,
    handleMoveTo,
    handleCampaignSelected,
    executeCampaignMove,
    showCampaigns,
    showReview,
    reviewContacts,
    selectedCampaign,
    moveMode,
    isGeneratingMoveScores,
    isMoving,
    handleReviewBack,
    resetSelectionAndClose: closeCampaignModal,
    showFastProspectingDrawer,
    setShowFastProspectingDrawer,
    handleGenerateMatchScore,
    isGeneratingMatchScore,
    hasNoCampaigns,
  };
};

export default useContactsPage;
